import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  Clock,
  ChevronLeft,
  ChevronRight,
  Check,
  User,
  Globe,
  Smartphone,
  Sparkles,
  CalendarDays,
} from 'lucide-react';

const timeSlots = [
  { time: '9:00 AM', provider: 'Dr. Martinez', type: 'Available', duration: '60 min' },
  { time: '10:30 AM', provider: 'Dr. Patel', type: 'Available', duration: '30 min' },
  { time: '11:00 AM', provider: 'Dr. Martinez', type: 'Available', duration: '60 min' },
  { time: '1:00 PM', provider: 'Dr. Patel', type: 'Available', duration: '30 min' },
  { time: '2:30 PM', provider: 'Dr. Martinez', type: 'Available', duration: '60 min' },
  { time: '3:30 PM', provider: 'Dr. Patel', type: 'Available', duration: '30 min' },
];

const calendarDays = [
  [null, null, 1, 2, 3, 4, 5],
  [6, 7, 8, 9, 10, 11, 12],
  [13, 14, 15, 16, 17, 18, 19],
  [20, 21, 22, 23, 24, 25, 26],
  [27, 28, 29, 30, 31, null, null],
];

const bookedDays = [3, 8, 14, 22, 28];
const selectedDay = 17;

const OnlineSchedulingSlide = () => {
  const [animStep, setAnimStep] = useState(0);
  // 0: calendar visible, 1: time slot selected, 2: confirmed

  useEffect(() => {
    const timers = [
      setTimeout(() => setAnimStep(1), 2500),
      setTimeout(() => setAnimStep(2), 5000),
      setTimeout(() => setAnimStep(0), 8500),
    ];

    const interval = setInterval(() => {
      setAnimStep(0);
      const t1 = setTimeout(() => setAnimStep(1), 2500);
      const t2 = setTimeout(() => setAnimStep(2), 5000);
      const t3 = setTimeout(() => setAnimStep(0), 8500);
      timers.push(t1, t2, t3);
    }, 8500);

    return () => {
      timers.forEach(clearTimeout);
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="h-full w-full bg-slate-900 flex items-center justify-center p-12 relative overflow-hidden text-white">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />

      <div className="flex flex-row h-full w-full max-w-[1500px] gap-12 relative z-10">
        {/* Left Side: Context */}
        <div className="w-2/5 flex flex-col justify-center space-y-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30 text-sm font-semibold w-fit">
            <CalendarDays className="w-4 h-4" />
            <span>Online Scheduling</span>
          </div>

          <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
            Patients book{' '}
            <span className="text-blue-400">on their own time.</span>
          </h2>

          <p className="text-lg text-slate-300 leading-relaxed">
            A beautiful, branded scheduling page your patients can access 24/7 — from your website, a text link, or a QR code in the office. No phone tag, no hold music, no back-and-forth.
          </p>

          <div className="space-y-4 pt-4">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400 mt-1">
                <Globe className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Embeddable & Shareable</h3>
                <p className="text-slate-400 text-sm">
                  Embed on your website, share via text or email. Works on any device, no app download required.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="p-2 bg-indigo-500/20 rounded-lg text-indigo-400 mt-1">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Smart Availability</h3>
                <p className="text-slate-400 text-sm">
                  Real-time sync with your schedule. Only shows open slots that match the appointment type and provider.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="p-2 bg-purple-500/20 rounded-lg text-purple-400 mt-1">
                <Smartphone className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Auto-Confirm & Remind</h3>
                <p className="text-slate-400 text-sm">
                  Instant confirmation texts. Automated reminders reduce no-shows without any staff effort.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Animated Scheduling Widget */}
        <div className="w-3/5 h-full flex items-center">
          <motion.div
            initial={{ x: 40, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="w-full h-[680px] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col text-slate-900"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-5 flex-shrink-0">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-white leading-tight">
                    Asha Dental
                  </h3>
                  <p className="text-blue-100 text-xs">Book Your Appointment Online</p>
                </div>
              </div>
            </div>

            {/* Appointment Type Selector */}
            <div className="px-6 py-4 border-b border-slate-100 flex-shrink-0">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                Appointment Type
              </p>
              <div className="flex gap-2">
                {['Cleaning', 'Exam', 'Whitening', 'Emergency'].map((type, i) => (
                  <motion.button
                    key={type}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + i * 0.08 }}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                      i === 0
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {type}
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Calendar + Slots */}
            <div className="flex-1 flex overflow-hidden">
              {/* Calendar */}
              <div className="w-[55%] px-6 py-4 border-r border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <button className="p-1 rounded-md hover:bg-slate-100 text-slate-400">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm font-bold text-slate-800">January 2025</span>
                  <button className="p-1 rounded-md hover:bg-slate-100 text-slate-400">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                {/* Day headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                    <div key={d} className="text-center text-[10px] font-bold text-slate-400 uppercase">
                      {d}
                    </div>
                  ))}
                </div>

                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.flat().map((day, idx) => {
                    const isSelected = day === selectedDay;
                    const isBooked = bookedDays.includes(day);
                    const isPast = day && day < 6;
                    return (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.1 + idx * 0.012 }}
                        className={`w-full aspect-square flex items-center justify-center rounded-lg text-xs font-medium transition-all cursor-pointer ${
                          !day
                            ? ''
                            : isSelected
                            ? 'bg-blue-600 text-white shadow-md shadow-blue-200'
                            : isBooked
                            ? 'bg-blue-50 text-blue-600 font-bold'
                            : isPast
                            ? 'text-slate-300'
                            : 'text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        {day}
                        {isBooked && !isSelected && (
                          <div className="absolute bottom-0.5 w-1 h-1 bg-blue-400 rounded-full" />
                        )}
                      </motion.div>
                    );
                  })}
                </div>

                {/* Selected date label */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                  className="mt-4 flex items-center gap-2 text-sm text-slate-500"
                >
                  <CalendarDays className="w-4 h-4 text-blue-500" />
                  <span>
                    <span className="font-semibold text-slate-800">Friday, Jan 17</span> — 6 slots available
                  </span>
                </motion.div>
              </div>

              {/* Time Slots */}
              <div className="w-[45%] px-5 py-4 overflow-y-auto">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                  Available Times
                </p>

                <div className="space-y-2">
                  {timeSlots.map((slot, i) => {
                    const isSelected = animStep >= 1 && i === 1;
                    const isConfirmed = animStep >= 2 && i === 1;
                    return (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6 + i * 0.1 }}
                        className={`relative rounded-xl border p-3 transition-all cursor-pointer ${
                          isConfirmed
                            ? 'border-blue-300 bg-blue-50'
                            : isSelected
                            ? 'border-blue-400 bg-blue-50 shadow-md shadow-blue-100'
                            : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                isConfirmed
                                  ? 'bg-blue-500 text-white'
                                  : isSelected
                                  ? 'bg-blue-100 text-blue-600'
                                  : 'bg-slate-100 text-slate-500'
                              }`}
                            >
                              {isConfirmed ? (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                                >
                                  <Check className="w-4 h-4" />
                                </motion.div>
                              ) : (
                                <Clock className="w-4 h-4" />
                              )}
                            </div>
                            <div>
                              <p className="text-sm font-bold text-slate-800">{slot.time}</p>
                              <p className="text-[11px] text-slate-400">
                                {slot.provider} &middot; {slot.duration}
                              </p>
                            </div>
                          </div>

                          <AnimatePresence mode="wait">
                            {isConfirmed ? (
                              <motion.span
                                key="confirmed"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0 }}
                                className="text-[10px] font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full uppercase tracking-wider"
                              >
                                Booked
                              </motion.span>
                            ) : isSelected ? (
                              <motion.span
                                key="selected"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0 }}
                                className="text-[10px] font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full uppercase tracking-wider"
                              >
                                Selected
                              </motion.span>
                            ) : null}
                          </AnimatePresence>
                        </div>

                        {/* Selection ring animation */}
                        {isSelected && !isConfirmed && (
                          <motion.div
                            className="absolute inset-0 rounded-xl border-2 border-blue-400 pointer-events-none"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: [0, 1, 0.6] }}
                            transition={{ duration: 0.6 }}
                          />
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Bottom Bar */}
            <AnimatePresence>
              {animStep >= 1 && (
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 20, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                  className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between flex-shrink-0"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <User className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">
                        Cleaning &middot; Dr. Patel
                      </p>
                      <p className="text-xs text-slate-400">
                        Fri, Jan 17 at 10:30 AM &middot; 30 min
                      </p>
                    </div>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className={`px-5 py-2.5 rounded-xl text-sm font-bold shadow-md transition-colors ${
                      animStep >= 2
                        ? 'bg-blue-500 text-white'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {animStep >= 2 ? (
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex items-center gap-2"
                      >
                        <Check className="w-4 h-4" />
                        Confirmed!
                      </motion.span>
                    ) : (
                      'Confirm Booking'
                    )}
                  </motion.button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default OnlineSchedulingSlide;
